import { AuthedButton } from '../authed-button';
import { IS_DEBUG, APP_CONTRACTS } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { useTradeGate } from '@/client/hooks/use-trade-gate';
import { useWallet } from '@/client/hooks/use-wallet';
import { getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { publicClient } from '@/client/utils/chain-client';
import { refetchQuery, refetchSignedUserAssets, refetchUserPositions } from '@/client/utils/query-status';
import { isBoundWalletMismatch, truncateWallet } from '@/client/utils/wallet-address';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { MarketStatus } from '@/libs/sdk/api';
import { predictionAbi } from '@/libs/sdk/contracts/abis/prediction';
import confetti from 'canvas-confetti';
import { useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Address } from 'viem';

// CTF redeemPositions ABI (for PredictionCTF markets)
const redeemPositionsAbi = [
  {
    type: 'function' as const,
    name: 'redeemPositions' as const,
    stateMutability: 'nonpayable' as const,
    inputs: [
      { name: 'collateralToken', type: 'address' as const },
      { name: 'parentCollectionId', type: 'bytes32' as const },
      { name: 'conditionId', type: 'bytes32' as const },
      { name: 'indexSets', type: 'uint256[]' as const },
    ],
    outputs: [],
  },
] as const;

type ClaimButtonProps = {
  marketAddress: string;
  optionAddress: string;
  disabled?: boolean;
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ClaimButton = ({ marketAddress, optionAddress, disabled = false }: ClaimButtonProps) => {
  const [claimed, setClaimed] = useState(false);
  const [isTrading, setIsTrading] = useState(false);
  const { t } = useTranslation();
  const { walletClient } = useWallet();
  const { linkedWallet } = useAuth();
  const tradeGate = useTradeGate();
  const market = useMarketDetail(api, { marketAddress });
  // redeemPositions collateral MUST be this market's own base token — using the
  // global toggle token on a mixed multi-base site would redeem nothing
  const baseTokenAddress = market.data?.baseTokenAddress;

  const CTF_ADDRESS = APP_CONTRACTS.CTF as Address | undefined;

  // Claim is allowed if market is closed (block-listener handles settlement detection)
  const canClaim = market.data?.marketStatus === MarketStatus.closed;

  const handleClaim = async () => {
    // Fail-closed funnel (ADR-051 D2 hardening): claim does NOT go through the
    // option-button gate, so run it here. tradeGate blocks when the binding is
    // unproven (DID /me still loading → canTrade false), closing the window where
    // the mismatch check below would pass on a null linkedWallet.
    if (!tradeGate()) return false;
    if (!walletClient?.account) {
      toast.error(t('wallet-not-connected', 'Please connect your wallet'));
      return false;
    }
    // Wrong-wallet backstop (ADR-051 D2): redeem must run from the bound wallet,
    // otherwise the connected wallet holds no option tokens and the tx reverts.
    // Kept as defense-in-depth for the actual signing account (tradeGate reads the
    // reactive wagmi address, which can lag walletClient by one render on switch).
    if (isBoundWalletMismatch(linkedWallet, walletClient.account.address)) {
      toast.error(
        t('wrong-wallet', 'Wrong wallet connected. Switch to your linked wallet {{wallet}} to trade.', {
          wallet: truncateWallet(linkedWallet),
        })
      );
      return false;
    }
    try {
      setIsTrading(true);

      const account = walletClient.account;
      let txHash: `0x${string}`;
      const conditionId = market.data?.conditionId as `0x${string}` | undefined;

      // Guard: ensure market data matches the target market (prevents stale cache from
      // sending the wrong conditionId when user switches markets quickly in wallet panel)
      if (market.data?.marketAddress && market.data.marketAddress !== marketAddress) {
        toast.error(t('claim-data-stale', 'Market data is loading. Please try again.'));
        return false;
      }

      if (CTF_ADDRESS && !baseTokenAddress) {
        toast.error(t('claim-data-incomplete', 'Market data incomplete. Please contact support.'));
        return false;
      }

      if (CTF_ADDRESS && !conditionId) {
        // PredictionCTF market but conditionId not synced -- cannot use legacy claimAll
        toast.error(t('claim-data-incomplete', 'Market data incomplete. Please contact support.'));
        return false;
      }

      if (CTF_ADDRESS && conditionId) {
        // PredictionCTF path: redeem via ConditionalTokens.redeemPositions
        const outcomeCount = market.data?.outcomeSlotCount ?? 2;
        const indexSets = Array.from({ length: outcomeCount }, (_, i) => BigInt(1 << i));
        const redeemArgs = {
          address: CTF_ADDRESS,
          abi: redeemPositionsAbi,
          functionName: 'redeemPositions' as const,
          args: [
            baseTokenAddress as Address,
            '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`,
            conditionId,
            indexSets,
          ] as const,
          account,
        };
        const redeemGas = await publicClient.estimateContractGas(redeemArgs);
        txHash = await walletClient.writeContract({
          ...redeemArgs,
          chain: walletClient.chain,
          gas: (redeemGas * 130n) / 100n,
        });
      } else {
        // Legacy Prediction path: claimAll
        const claimArgs = {
          address: marketAddress as Address,
          abi: predictionAbi,
          functionName: 'claimAll' as const,
          args: [] as const,
          account,
        };
        const claimGas = await publicClient.estimateContractGas(claimArgs);
        txHash = await walletClient.writeContract({
          ...claimArgs,
          chain: walletClient.chain,
          gas: (claimGas * 130n) / 100n,
        });
      }
      await publicClient.waitForTransactionReceipt({ hash: txHash });

      toast.success(t('claim-success'));
      setClaimed(true);

      // Refresh related queries so UI updates automatically
      await Promise.allSettled([
        refetchQuery('MarketDetail'),
        refetchUserPositions(),
        refetchSignedUserAssets(),
        refetchQuery('UserAssetsInfo'),
        refetchQuery('HoldingPositionsMarket'),
      ]);

      return true;
    } catch (error) {
      if (IS_DEBUG) console.log('claim error', error);
      const msg = getErrorMessage(error);
      toast.error(msg ?? t('claim-failed'));
      return false;
    } finally {
      setIsTrading(false);
    }
  };
  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const success = await handleClaim();
    if (success) {
      confetti({
        origin: {
          x: x / window.innerWidth,
          y: y / window.innerHeight,
        },
      });
    }
  };
  if (claimed)
    return (
      <AuthedButton
        disabled={true}
        variant="default"
        size="lg"
        className="mt-4 p-4 text-center text-sm rounded-3 font-semibold leading-none w-full opacity-60"
      >
        {t('claimed', 'Claimed')}
      </AuthedButton>
    );
  return (
    <AuthedButton
      disabled={isTrading || disabled || !canClaim}
      onClick={handleClick}
      isLoading={isTrading || market.isLoading}
      variant="default"
      size="lg"
      className="mt-4 p-4 text-center text-sm rounded-3 font-semibold leading-none w-full"
    >
      {canClaim
        ? t('claim-winnings')
        : market.data?.marketStatus === MarketStatus.disputed
          ? t('dispute-in-progress', 'Dispute in progress, awaiting arbitration')
          : t('processing-settlement', 'Processing settlement, please wait')}
    </AuthedButton>
  );
};
